/**
 * Product Sales Table Component
 *
 * Component for displaying product sales data in a sortable, searchable table
 */

import React, {useState, useMemo} from 'react';
import {ProductSalesSummary} from '../models/productSalesReport.model';
import AppTable from '../../../Presentation/components/ui/AppTable';
import {AppText} from '../../../Presentation/components/ui/Typography';
import {TableColumn} from '../../../Presentation/components/ui/AppTable';
import {formatCurrency, formatNumber} from '../../../Presentation/utils/numberUtils';
import AppInput from '../../../Presentation/components/ui/AppInput';

interface ProductSalesTableProps {
  products: ProductSalesSummary[];
}

type SortField = 'product_name' | 'product_brand' | 'product_category' | 'quantity_sold' | 'unit_price' | 'total_revenue' | 'stores_count' | 'orders_count';
type SortDirection = 'asc' | 'desc';

export const ProductSalesTable: React.FC<ProductSalesTableProps> = ({products}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState<SortField>('total_revenue');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  // Filter and sort products
  const filteredAndSortedProducts = useMemo(() => {
    let filtered = products;

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = products.filter(product =>
        product.product_name.toLowerCase().includes(query) ||
        product.product_brand?.toLowerCase().includes(query) ||
        product.product_category?.toLowerCase().includes(query)
      );
    }

    // Apply sorting
    const sorted = [...filtered].sort((a, b) => {
      let aValue: string | number;
      let bValue: string | number;

      switch (sortField) {
        case 'product_name':
          aValue = a.product_name.toLowerCase();
          bValue = b.product_name.toLowerCase();
          break;
        case 'product_brand':
          aValue = (a.product_brand || '').toLowerCase();
          bValue = (b.product_brand || '').toLowerCase();
          break;
        case 'product_category':
          aValue = (a.product_category || '').toLowerCase();
          bValue = (b.product_category || '').toLowerCase();
          break;
        case 'quantity_sold':
          aValue = a.quantity_sold;
          bValue = b.quantity_sold;
          break;
        case 'unit_price':
          aValue = a.unit_price;
          bValue = b.unit_price;
          break;
        case 'total_revenue':
          aValue = a.total_revenue;
          bValue = b.total_revenue;
          break;
        case 'stores_count':
          aValue = a.stores_count || 0;
          bValue = b.stores_count || 0;
          break;
        case 'orders_count':
          aValue = a.orders_count || 0;
          bValue = b.orders_count || 0;
          break;
        default:
          return 0;
      }

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortDirection === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      return sortDirection === 'asc'
        ? (aValue as number) - (bValue as number)
        : (bValue as number) - (aValue as number);
    });

    return sorted;
  }, [products, searchQuery, sortField, sortDirection]);

  // Calculate pagination
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedData = filteredAndSortedProducts.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
    setCurrentPage(1);
  };

  const columns: TableColumn<ProductSalesSummary>[] = [
    {
      key: 'product_name',
      title: (
        <button
          onClick={() => handleSort('product_name')}
          className="flex items-center gap-2 hover:text-brand-600"
        >
          اسم المنتج
          {sortField === 'product_name' && (
            <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
          )}
        </button>
      ),
      render: (product) => <AppText>{product.product_name}</AppText>
    },
    {
      key: 'product_brand',
      title: (
        <button
          onClick={() => handleSort('product_brand')}
          className="flex items-center gap-2 hover:text-brand-600"
        >
          العلامة التجارية
          {sortField === 'product_brand' && (
            <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
          )}
        </button>
      ),
      render: (product) => (
        <AppText>{product.product_brand || 'N/A'}</AppText>
      )
    },
    {
      key: 'product_category',
      title: (
        <button
          onClick={() => handleSort('product_category')}
          className="flex items-center gap-2 hover:text-brand-600"
        >
          الفئة
          {sortField === 'product_category' && (
            <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
          )}
        </button>
      ),
      render: (product) => (
        <AppText>{product.product_category || 'N/A'}</AppText>
      )
    },
    {
      key: 'quantity_sold',
      title: (
        <button
          onClick={() => handleSort('quantity_sold')}
          className="flex items-center gap-2 hover:text-brand-600"
        >
          الكمية المباعة
          {sortField === 'quantity_sold' && (
            <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
          )}
        </button>
      ),
      render: (product) => (
        <AppText>{formatNumber(product.quantity_sold)}</AppText>
      )
    },
    {
      key: 'unit_price',
      title: (
        <button
          onClick={() => handleSort('unit_price')}
          className="flex items-center gap-2 hover:text-brand-600"
        >
          سعر الوحدة
          {sortField === 'unit_price' && (
            <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
          )}
        </button>
      ),
      render: (product) => (
        <AppText>{formatCurrency(product.unit_price)}</AppText>
      )
    },
    {
      key: 'total_revenue',
      title: (
        <button
          onClick={() => handleSort('total_revenue')}
          className="flex items-center gap-2 hover:text-brand-600"
        >
          إجمالي الإيرادات
          {sortField === 'total_revenue' && (
            <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
          )}
        </button>
      ),
      render: (product) => (
        <AppText className="font-semibold text-brand-600">
          {formatCurrency(product.total_revenue)}
        </AppText>
      )
    },
    {
      key: 'stores_count',
      title: (
        <button
          onClick={() => handleSort('stores_count')}
          className="flex items-center gap-2 hover:text-brand-600"
        >
          عدد المتاجر
          {sortField === 'stores_count' && (
            <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
          )}
        </button>
      ),
      render: (product) => (
        <AppText>{product.stores_count ? formatNumber(product.stores_count) : 'N/A'}</AppText>
      )
    },
    {
      key: 'orders_count',
      title: (
        <button
          onClick={() => handleSort('orders_count')}
          className="flex items-center gap-2 hover:text-brand-600"
        >
          عدد الطلبات
          {sortField === 'orders_count' && (
            <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
          )}
        </button>
      ),
      render: (product) => (
        <AppText>{product.orders_count ? formatNumber(product.orders_count) : 'N/A'}</AppText>
      )
    }
  ];

  return (
    <div className="space-y-4">
      {/* Search Input */}
      <div className="flex items-center gap-4">
        <div className="flex-1 max-w-md relative">
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
            <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <AppInput
            type="text"
            placeholder="ابحث عن منتج، علامة تجارية، أو فئة..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setCurrentPage(1);
            }}
            className="pr-10"
          />
        </div>
        {searchQuery && (
          <AppText className="text-sm text-gray-600">
            {filteredAndSortedProducts.length} نتيجة
          </AppText>
        )}
      </div>

      {/* Table */}
      <AppTable
        columns={columns}
        data={paginatedData}
        rowKey={(product) => product.product_id}
        emptyText="لا توجد منتجات متاحة"
        hidePagination={false}
        page={currentPage}
        pageSize={pageSize}
        totalCount={filteredAndSortedProducts.length}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      />
    </div>
  );
};

